interface FontSizeEntry {
  fontSize: number
  lineHeight: string
}

interface ResponsiveFontSizeEntry extends FontSizeEntry {
  mobileFontSize: number
}

export interface FontContextConfig {
  hero: ResponsiveFontSizeEntry
  detailHero: ResponsiveFontSizeEntry
  heroEditPreview: FontSizeEntry
  card: ResponsiveFontSizeEntry
  coverFlow: FontSizeEntry
  persona: FontSizeEntry
}

const DEFAULTS: FontContextConfig = {
  hero:            { fontSize: 52, mobileFontSize: 36, lineHeight: '1.2' },
  detailHero:      { fontSize: 52, mobileFontSize: 32, lineHeight: '1.2' },
  heroEditPreview: { fontSize: 48,                     lineHeight: '1' },
  card:            { fontSize: 24, mobileFontSize: 20, lineHeight: '1.2' },
  coverFlow:       { fontSize: 32,                     lineHeight: '1' },
  persona:         { fontSize: 24,                     lineHeight: '1.3' },
}

/**
 * Per-font overrides — add entries here after live validation.
 *
 * Example:
 *   'Montserrat': {
 *     hero: { fontSize: 76, mobileFontSize: 48, lineHeight: '0.85' },
 *   },
 */
const FONT_OVERRIDES: Partial<Record<string, Partial<FontContextConfig>>> = {
  'Oswald': {
    hero:       { fontSize: 64, mobileFontSize: 44, lineHeight: '1.1' },
    detailHero: { fontSize: 64, mobileFontSize: 38, lineHeight: '1.1' },
    card:       { fontSize: 28, mobileFontSize: 22, lineHeight: '1.2' },
  },
}

export function getDisplayFontConfig(fontFamily: string | null): FontContextConfig {
  if (!fontFamily) return DEFAULTS
  const overrides = FONT_OVERRIDES[fontFamily]
  if (!overrides) return DEFAULTS
  return {
    hero:            { ...DEFAULTS.hero,            ...overrides.hero },
    detailHero:      { ...DEFAULTS.detailHero,      ...overrides.detailHero },
    heroEditPreview: { ...DEFAULTS.heroEditPreview, ...overrides.heroEditPreview },
    card:            { ...DEFAULTS.card,            ...overrides.card },
    coverFlow:       { ...DEFAULTS.coverFlow,       ...overrides.coverFlow },
    persona:         { ...DEFAULTS.persona,         ...overrides.persona },
  }
}

/**
 * Build CSS custom property style for responsive font sizing.
 * Pair with Tailwind classes:
 *   `text-[length:var(--{prefix}-fs-sm)] {breakpoint}:text-[length:var(--{prefix}-fs)]`
 */
export function responsiveFontVars(
  entry: ResponsiveFontSizeEntry,
  prefix: string,
  fontFamily?: string,
): React.CSSProperties {
  return {
    [`--${prefix}-fs`]: `${entry.fontSize}px`,
    [`--${prefix}-fs-sm`]: `${entry.mobileFontSize}px`,
    lineHeight: entry.lineHeight,
    ...(fontFamily ? { fontFamily } : {}),
  } as React.CSSProperties
}

/** Build inline style for non-responsive font sizing */
export function fontSizeStyle(
  entry: FontSizeEntry,
  fontFamily?: string,
): React.CSSProperties {
  return {
    fontSize: `${entry.fontSize}px`,
    lineHeight: entry.lineHeight,
    ...(fontFamily ? { fontFamily } : {}),
  }
}
